/* Supabase Auth with an email magic link (PKCE). A 6-digit code from the same email is accepted too. */
import { GoTrueClient, isAuthRetryableFetchError, type AuthChangeEvent, type Session } from "@supabase/auth-js";
import { RepoError } from "../storage/errors";
import type { CloudConfig } from "./config";

export const AUTH_STORAGE_KEY = "memolink-auth";
/** set by the tab that logs out, so other open tabs clear the screen too (an expired session does not set it) */
const LOGOUT_MARK = "memolink-logout-at";

/** only the Auth part of supabase-js; data goes through CloudRepository's own REST calls */
let client: { auth: GoTrueClient } | null = null;
export function authClient(cfg?: CloudConfig): { auth: GoTrueClient } {
  if (!client) {
    if (!cfg) throw new Error("auth not configured");
    client = { auth: new GoTrueClient({
      url: `${cfg.url}/auth/v1`,
      headers: { apikey: cfg.anonKey, Authorization: `Bearer ${cfg.anonKey}` },
      flowType: "pkce", persistSession: true, autoRefreshToken: true, detectSessionInUrl: true, storageKey: AUTH_STORAGE_KEY
    }) };
  }
  return client;
}

/** where the magic link sends the person back: this page, without query or hash */
export const redirectUrl = () => location.origin + location.pathname;

const linkErrorText = (desc: string) =>
  /expired|invalid/i.test(desc) ? "ลิงก์เข้าสู่ระบบหมดอายุหรือถูกใช้ไปแล้ว ขอลิงก์ใหม่ได้เลย"
  : /code verifier|both auth code and code verifier/i.test(desc) ? "ลิงก์นี้ต้องเปิดในเบราว์เซอร์เดียวกับที่ขอลิงก์ หรือใช้รหัส 6 หลักจากอีเมลแทน"
  : "เข้าสู่ระบบด้วยลิงก์นี้ไม่ได้ ขอลิงก์ใหม่ได้เลย";

/**
 * Restores the saved session (and finishes a magic-link sign-in if the URL carries one) before anything is shown.
 * Returns the session, or null plus a message when the link in the URL could not be used.
 */
export async function initAuth(cfg: CloudConfig): Promise<{ session: Session | null; linkError: string | null }> {
  const params = new URLSearchParams(location.search);
  const hash = new URLSearchParams(location.hash.replace(/^#/, ""));
  const urlErr = params.get("error_description") || hash.get("error_description");
  const hadCode = params.has("code");
  const c = authClient(cfg);
  const init = await c.auth.initialize();
  let linkError: string | null = urlErr ? linkErrorText(urlErr) : null;
  if (!linkError && hadCode && init.error) linkError = linkErrorText(init.error.message);
  if (urlErr || hadCode || hash.has("access_token") || hash.has("error")) history.replaceState(null, "", redirectUrl());
  const { data } = await c.auth.getSession();
  return { session: data.session, linkError: data.session ? null : linkError };
}

export type AuthResult = { ok: true } | { ok: false; message: string };
const authFail = (e: { status?: number; code?: string; message: string }): AuthResult => {
  if (isAuthRetryableFetchError(e)) return { ok: false, message: "เชื่อมต่อไม่ได้ ตรวจอินเทอร์เน็ตแล้วลองอีกครั้ง" };
  if (e.status === 429 || /rate limit|security purposes/i.test(e.message)) return { ok: false, message: "ขอบ่อยเกินไป รอสักครู่แล้วลองอีกครั้ง" };
  if (e.code === "otp_expired" || /expired|invalid/i.test(e.message)) return { ok: false, message: "รหัสไม่ถูกต้องหรือหมดอายุ ขอลิงก์ใหม่ได้" };
  if (/email/i.test(e.message) && e.status === 400) return { ok: false, message: "อีเมลไม่ถูกต้อง" };
  return { ok: false, message: "เข้าสู่ระบบไม่ได้ ลองอีกครั้ง" };
};

export async function sendMagicLink(email: string): Promise<AuthResult> {
  try {
    const { error } = await authClient().auth.signInWithOtp({ email, options: { emailRedirectTo: redirectUrl(), shouldCreateUser: true } });
    return error ? authFail(error) : { ok: true };
  } catch { return { ok: false, message: "เชื่อมต่อไม่ได้ ตรวจอินเทอร์เน็ตแล้วลองอีกครั้ง" }; }
}

export async function verifyCode(email: string, code: string): Promise<AuthResult> {
  try {
    const { error } = await authClient().auth.verifyOtp({ email, token: code, type: "email" });
    return error ? authFail(error) : { ok: true };
  } catch { return { ok: false, message: "เชื่อมต่อไม่ได้ ตรวจอินเทอร์เน็ตแล้วลองอีกครั้ง" }; }
}

/** Ends the session on this device only (other devices stay signed in). */
export async function signOutHere() {
  try { localStorage.setItem(LOGOUT_MARK, String(Date.now())); } catch { /* storage blocked */ }
  try { await authClient().auth.signOut({ scope: "local" }); } catch { /* offline: the local session is removed anyway */ }
}
/** true when a SIGNED_OUT came from someone pressing "log out" (here or in another tab), not from an expired session */
export function wasExplicitLogout(): boolean {
  try { return Date.now() - Number(localStorage.getItem(LOGOUT_MARK) || 0) < 15_000; } catch { return false; }
}

export function onAuthChange(cb: (event: AuthChangeEvent, session: Session | null) => void) {
  // never call back into supabase from inside its own callback
  return authClient().auth.onAuthStateChange((event, session) => { setTimeout(() => cb(event, session), 0); });
}

/**
 * Access token for a request made on behalf of `userId`. Refuses when the device is now signed in as someone else,
 * so a request prepared for one account can never be sent with another account's token.
 */
export async function accessTokenFor(userId: string): Promise<string> {
  let res;
  try { res = await authClient().auth.getSession(); }
  catch { throw new RepoError("offline"); }
  if (res.error && isAuthRetryableFetchError(res.error)) throw new RepoError("offline");
  const s = res.data.session;
  if (!s) throw new RepoError("session");
  if (s.user.id !== userId) throw new RepoError("account");
  return s.access_token;
}
